import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { WorshipReport } from '../../types/worshipReport';

interface WorshipReportSelectProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  departmentCollection: string;
}

export function WorshipReportSelect({ value, onChange, required, disabled, departmentCollection }: WorshipReportSelectProps) {
  const [worshipReports, setWorshipReports] = useState<WorshipReport[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchWorshipReports = async () => {
      setLoading(true);
      try {
        const [worshipSnap, deptSnap] = await Promise.all([
          getDocs(query(collection(db, 'worshipReports'), orderBy('date', 'desc'))),
          getDocs(collection(db, departmentCollection)),
        ]);

        const usedIds = new Set(
          deptSnap.docs.map(d => d.data().worshipReportId).filter(Boolean)
        );

        const available = worshipSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as WorshipReport))
          .filter(r => !usedIds.has(r.id) || r.id === value);

        setWorshipReports(available);
      } catch (error) {
        console.error('Error fetching worship reports:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWorshipReports();
  }, [departmentCollection, value]);

  const formatReportLabel = (report: WorshipReport) => {
    const date = new Date(report.date).toLocaleDateString('fr-FR');
    return `${date} - ${report.messageTheme}`;
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-600 mb-1">
        Rapport de culte à annexer *
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={loading || disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C] disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        <option value="">
          {loading ? 'Chargement des rapports...' : 'Sélectionner le rapport de culte'}
        </option>
        {worshipReports.map((report) => (
          <option key={report.id} value={report.id}>
            {formatReportLabel(report)}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-gray-500">
        Seuls les cultes sans rapport de département rattaché sont affichés.
      </p>
    </div>
  );
}
